import { setCookie } from '@/lib/cookies'
import { queryClient } from '@/main'
import { useAppStore } from './app-store'
import type { Locale } from 'shared/types'
import { isLocale, locales } from 'shared/locales'
import { i18n, setupI18n, type I18n, type Messages } from '@lingui/core'
import { useLingui } from '@lingui/react'
import { useRouter } from '@tanstack/react-router'
import { isSeoPath, localePath, splitLocalePath } from './locale-path'
import { loadChunkCatalogs } from './i18n-catalogs'

export function useTranslation() {
  const { i18n: activeI18n } = useLingui()
  const setStore = useAppStore((state) => state.setStore)
  const router = useRouter()

  const language: Locale = isLocale(activeI18n.locale) ? activeI18n.locale : 'es-ES'

  /**
   * On a public page the locale is part of the URL, so switching it is a NAVIGATION — anything less would leave
   * the address bar claiming a language the page no longer renders. Everywhere else the cookie is the only signal,
   * and the router has to be invalidated as well as the cache: `head()` is recomputed per match, so without it the
   * document title stayed in the previous language while the body switched.
   */
  const changeLanguage = (newLocale: string) => {
    setCookie('language', newLocale)

    const { path } = splitLocalePath(window.location.pathname)
    if (isSeoPath(path)) {
      window.location.assign(localePath(path, newLocale as Locale) + window.location.search + window.location.hash)
      return
    }

    loadTranslation(newLocale as Locale)
    setStore({ locale: newLocale as Locale })
    queryClient.invalidateQueries()
    void router.invalidate()
  }

  return {
    language,
    changeLanguage,
    languages: {
      'es-ES': 'Español',
      'ca-ES': 'Català',
      'en-US': 'English',
      'pt-BR': 'Português',
    },
  }
}

// One instance per SERVER request: a shared catalog is mutable global state, so interleaved SSR renders clobber
// each other's active locale and the client then hydrates against the wrong language (#418).
export function createLocaleI18n(): I18n {
  return typeof document === 'undefined' ? setupI18n() : i18n
}

/**
 * Catalog memo, production only. The `import()` below resolves an already-evaluated module every call
 * after the first, but the machinery is not free: ~1.2µs per repeat import in Bun against ~3ns for a
 * memoized reference, and `createRouter` calls it once per SSR request — ~0.5% of on-CPU on /report.
 *
 * DEV is excluded so editing a `.po` file still hot-reloads; `import.meta.env.DEV` is statically false
 * in the build, so the branch is eliminated in production.
 */
const catalogCache = new Map<Locale, Messages>()

/** Every locale's catalog into the cache before the server listens, so no first request pays its import. */
export async function preloadCatalogs(): Promise<void> {
  for (const locale of locales) {
    if (catalogCache.has(locale)) continue
    // oxlint-disable-next-line no-await-in-loop -- one ~200 KB catalog at a time, as preloadTranslations does
    const { messages } = (await import(`../locales/${locale}.po`)) as { messages: Messages }
    catalogCache.set(locale, messages)
  }
}

/**
 * Load and activate a locale's catalog on the instance that will render it, and ONLY that one.
 *
 * On the client `createLocaleI18n` hands back the shared global. On the server `instance` is
 * request-scoped and the global is deliberately left alone: activating process-wide state per request let
 * concurrent renders clobber each other's locale, the race {@link createLocaleI18n} exists to contain.
 */
export async function loadTranslation(locale: Locale, instance?: I18n): Promise<I18n> {
  // The built client never loads a whole catalog: each chunk brings the messages it renders, and this gathers the
  // loaded chunks' catalogs in `locale` (fetching them on a switch) — docs/notes/app/i18n-catalogs.md. Dev keeps the
  // `.po` import below for hot reload, and the server holds every locale in memory.
  if (!import.meta.env.SSR && !import.meta.env.DEV) {
    const target = instance ?? i18n
    target.loadAndActivate({ locale, messages: await loadChunkCatalogs(locale) })
    return target
  }
  let messages = import.meta.env.DEV ? undefined : catalogCache.get(locale)
  if (!messages) {
    ;({ messages } = (await import(`../locales/${locale}.po`)) as { messages: Messages })
    if (!import.meta.env.DEV) catalogCache.set(locale, messages)
  }
  const target = instance ?? i18n
  // Still per-request: `instance` is request-scoped on the server (see createLocaleI18n), so the catalog
  // must be activated on it every time. Only the module resolution is memoized, never the activation.
  target.loadAndActivate({ locale, messages })
  return target
}
